/**
 * 放送地点一覧の JSON Import/Export ボタン群。
 *
 * - Export: 親から渡された一覧をそのまま JSON ファイル化してダウンロードする。
 * - Import: ファイルを選択 → パース → 確認 → 親から渡された一括投入関数 (POST/PUT)
 *   を順次実行する。エラー件数 / 成功件数を結果としてコールバックする。
 */
import { useRef, useState } from 'react';

import type { BroadcastPoint } from '../api/types';
import { exportBroadcastPointsAsJson, parseImportJson } from '../utils/jsonImportExport';

interface ImportExportControlProps {
  /** Export 対象の現在の一覧 */
  items: BroadcastPoint[];
  /** Import 用の一括 upsert 関数 (実装は親側で API を呼ぶ)
   *  spotId が存在すれば PUT、なければ POST で新規作成する想定。
   */
  onImport: (items: BroadcastPoint[]) => Promise<{ success: number; failures: string[] }>;
}

export function ImportExportControl(props: ImportExportControlProps): React.JSX.Element {
  const { items, onImport } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleExport = (): void => {
    exportBroadcastPointsAsJson(items);
  };

  const handleImportClick = (): void => {
    setResultMessage(null);
    setErrorMessage(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    let parsed: BroadcastPoint[];
    try {
      const text = await file.text();
      parsed = parseImportJson(text);
    } catch (err) {
      setErrorMessage((err as Error).message);
      return;
    }

    if (parsed.length === 0) {
      setErrorMessage('JSON に放送地点が含まれていません');
      return;
    }

    if (!window.confirm(`${parsed.length} 件の放送地点を Import します。よろしいですか？`)) {
      return;
    }

    setImporting(true);
    try {
      const result = await onImport(parsed);
      const failureCount = result.failures.length;
      if (failureCount === 0) {
        setResultMessage(`Import が完了しました (成功: ${result.success} 件)`);
      } else {
        setResultMessage(
          `Import 完了 (成功: ${result.success} 件 / 失敗: ${failureCount} 件)\n失敗詳細: ${result.failures.join(' / ')}`,
        );
      }
    } catch (err) {
      setErrorMessage((err as Error).message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="export-button"
        onClick={handleExport}
        disabled={items.length === 0}
      >
        JSON Export
      </button>
      <button
        type="button"
        data-testid="import-button"
        onClick={handleImportClick}
        disabled={importing}
      >
        {importing ? 'Import 中...' : 'JSON Import'}
      </button>
      <input
        type="file"
        data-testid="import-file-input"
        accept="application/json"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      {errorMessage ? (
        <div className="error-message" style={{ flexBasis: '100%' }}>
          {errorMessage}
        </div>
      ) : null}
      {resultMessage ? (
        <div className="success-message" style={{ flexBasis: '100%', whiteSpace: 'pre-wrap' }}>
          {resultMessage}
        </div>
      ) : null}
    </>
  );
}
